import { useTranslation } from 'react-i18next';
import { FileText } from 'lucide-react';
import { FieldSeparator } from '@bliks-io/ui';
import { FormArtifactTypeSelect, FormInput, FormTextarea } from '@/components/form/rhf';
import {
  AddArtifactRequestSchema,
  type AddArtifactRequest,
  type UpdateArtifactRequest,
  type Artifact
} from '@bliks-io/contracts';
import { FormDialog } from '@/components/dialogs/FormDialog';

interface ArtifactFormDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSubmit: (data: AddArtifactRequest | UpdateArtifactRequest) => unknown;
  artifact?: Artifact;
  /** Pre-fill the name field when creating a new artifact (e.g. from a picker's search query). */
  initialName?: string;
}

export function ArtifactFormDialog({
  open,
  onOpenChange,
  onSubmit,
  artifact,
  initialName
}: ArtifactFormDialogProps) {
  const { t } = useTranslation();
  const isEditing = !!artifact;

  const defaultValues: AddArtifactRequest = {
    name: artifact?.name || initialName || '',
    description: artifact?.description || '',
    type: artifact?.type || '',
    fileUrl: artifact?.fileUrl || ''
  };

  return (
    <FormDialog<AddArtifactRequest>
      open={open}
      onOpenChange={onOpenChange}
      title={
        isEditing ? t('organization.artifacts.editArtifact') : t('organization.artifacts.create')
      }
      description={
        isEditing
          ? t('organization.artifacts.editArtifactDescription')
          : t('organization.artifacts.createArtifactDescription')
      }
      icon={FileText}
      schema={AddArtifactRequestSchema}
      defaultValues={defaultValues}
      onSubmit={onSubmit}
      isEditing={isEditing}
      size="md"
    >
      {({ form }) => (
        <div className="flex flex-col gap-8">
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <FormInput
              control={form.control}
              name="name"
              label={t('common.Name')}
              placeholder={t('organization.artifacts.namePlaceholder')}
              required
            />
            <FormArtifactTypeSelect
              control={form.control}
              name="type"
              label={t('organization.artifacts.type')}
              placeholder={t('organization.artifacts.typePlaceholder')}
            />
          </div>

          <div className="flex flex-col gap-4">
            <FieldSeparator>{t('common.Details')}</FieldSeparator>

            <FormTextarea
              control={form.control}
              name="description"
              label={t('common.Description')}
              placeholder={t('organization.artifacts.descriptionPlaceholder')}
              rows={4}
            />

            <FormInput
              control={form.control}
              name="fileUrl"
              label={t('organization.artifacts.fileUrl')}
              placeholder={t('organization.artifacts.fileUrlPlaceholder')}
            />
          </div>
        </div>
      )}
    </FormDialog>
  );
}
